import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { ReactNode, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { UnitCreationCard } from 'library/components/controls/cards/UnitCreationCard';
import { IControl } from 'library/components/controls/common/IControl';
import { Removable } from 'library/types';
import { PCTheme, textOverflowSx } from 'library/utils/themes/PCTheme';
import { ForeignProductIndividualDto } from 'modules/Shared/State/unit/Types';

const {
	palette: { grey },
} = PCTheme;

interface ProductIndividualCardProps extends IControl, Removable {
	actions?: ReactNode;
	product: ForeignProductIndividualDto;
}

export const ForeignProductIndividualCard = memo(
	({
		actions,
		isRemoved,
		product: { serialNumber, partNumber, description, type, generatedUuid },
	}: ProductIndividualCardProps) => {
		const { t } = useTranslation(['common', 'productType']);

		return (
			<UnitCreationCard
				variant="outlined"
				color="default"
				sx={[
					isRemoved ? { pointerEvents: 'none' } : null,
					{
						backgroundColor: isRemoved ? grey[50] : 'default',
						maxWidth: 20,
					},
				]}
				data-testid={`FPI-${serialNumber || generatedUuid}`}>
				<CardHeader
					action={actions || <></>}
					title={
						<Typography
							variant="h6"
							sx={{
								fontSize: 16,
							}}>
							{t(`productType:${type.toString()}` as never)}
						</Typography>
					}
				/>
				<CardContent>
					<Typography
						component="p"
						variant="subtitle2"
						sx={[
							{
								fontWeight: 400,
								...textOverflowSx,
							},
						]}>
						{t('common:serial-number')}
						{': '}
						<strong>{serialNumber}</strong>
					</Typography>
					<Typography
						component="p"
						variant="subtitle2"
						sx={[
							{
								fontWeight: 400,
								...textOverflowSx,
							},
						]}>
						{t('common:product-number')}
						{': '}
						<strong>{partNumber}</strong>
					</Typography>
					<Typography
						component="p"
						variant="subtitle2"
						sx={[
							{
								fontWeight: 400,
								...textOverflowSx,
							},
						]}>
						{t('common:description')}
						{': '}
						<strong>{description}</strong>
					</Typography>
				</CardContent>
			</UnitCreationCard>
		);
	}
);
